import React from 'react';
import { useTranslation } from 'react-i18next';
import { PACKAGES_META } from '../data/mock';
import { Check, ArrowUpRight } from 'lucide-react';

const PackageCard = ({ meta, data, mostChosenLabel }) => {
  const Icon = meta.icon;
  const isPlatinum = meta.highlight;
  return (
    <div
      data-testid={`pricing-card-${meta.id}`}
      className={`relative rounded-2xl p-8 md:p-10 transition-all duration-300 ${
        isPlatinum
          ? 'panel-card border-[#5FE0C8]/40 hover:border-[#5FE0C8]/70 shadow-[0_30px_80px_-30px_rgba(95,224,200,0.35)]'
          : 'panel-card hover:border-[#C9A14A]/40'
      }`}
    >
      {isPlatinum && (
        <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#5FE0C8] text-[#07120F] text-[11px] tracking-[0.18em] uppercase font-medium">
          {mostChosenLabel}
        </div>
      )}

      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-3">
          <span
            className="w-11 h-11 rounded-full grid place-items-center border"
            style={{
              borderColor: isPlatinum ? 'rgba(95,224,200,0.4)' : 'rgba(201,161,74,0.4)',
              background: isPlatinum ? 'rgba(95,224,200,0.08)' : 'rgba(201,161,74,0.08)',
            }}
          >
            <Icon className="w-5 h-5" strokeWidth={1.6} style={{ color: meta.accent }} />
          </span>
          <h3 className="font-display text-[28px] tracking-tight" style={{ color: meta.accent }}>
            {data.tier}
          </h3>
        </div>
      </div>

      <p className="text-[14.5px] text-[#C9C5B8] leading-relaxed min-h-[68px]">{data.summary}</p>

      <div className="mt-7 pb-7 border-b border-white/5">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-[52px] md:text-[60px] leading-none text-[#ECE9DF]">{meta.fee}</span>
          <span className="text-[13px] text-[#8A9690]">{data.feeNote}</span>
        </div>
        <div className="mt-3 flex items-baseline gap-2 text-[14px]">
          <span className="text-[#ECE9DF] font-medium">{data.retainer}</span>
          <span className="text-[12px] text-[#7E8A85]">· {data.retainerNote}</span>
        </div>
      </div>

      <ul className="mt-7 space-y-3.5">
        {data.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-[14px] text-[#C9C5B8] leading-snug">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: meta.accent }} strokeWidth={2.2} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href="#contact"
        data-testid={`pricing-cta-${meta.id}`}
        className={`mt-9 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[14px] font-medium transition-all ${
          isPlatinum ? 'btn-mint' : 'btn-gold'
        }`}
      >
        {data.cta} <ArrowUpRight className="w-4 h-4" />
      </a>
    </div>
  );
};

const Pricing = () => {
  const { t } = useTranslation();
  const packages = t('pricing.packages', { returnObjects: true });
  return (
    <section id="pricing" className="relative py-24 md:py-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-[680px] mx-auto mb-14 md:mb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#5FE0C8] mb-4">{t('pricing.eyebrow')}</div>
          <h2 className="font-display text-[44px] md:text-[64px] leading-[0.98] tracking-tight">
            {t('pricing.heading1')} <span className="font-serif-i text-[#5FE0C8]">{t('pricing.heading2')}</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#9FA8A4] leading-relaxed">
            {t('pricing.intro')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {PACKAGES_META.map((meta) => (
            <PackageCard
              key={meta.id}
              meta={meta}
              data={packages[meta.id]}
              mostChosenLabel={t('pricing.mostChosen')}
            />
          ))}
        </div>

        <p className="mt-10 text-center text-[12.5px] text-[#7E8A85] max-w-[640px] mx-auto leading-relaxed">
          <span className="text-[#5FE0C8]">{t('pricing.noteLabel')}</span> {t('pricing.footnote')}
        </p>
      </div>
    </section>
  );
};

export default Pricing;
